import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Feather } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { Reveal, RevealLine } from "@/components/Reveal";
import { SectionHead } from "@/components/Sections";
import { QcmModal } from "@/components/QcmModal";
import { ContactForm } from "@/components/ContactForm";
import { scrollToTarget } from "@/lib/smooth";

const MARQUEE_ITEMS = [
  "Gagner du temps",
  "Trouver les bons mots",
  "Être accompagné",
  "Regard extérieur",
  "Améliorer vos écrits",
  "Faire rédiger ou corriger",
  "Suivi de vos démarches",
];

const SERVICES = [
  {
    id: "lettres-courriers",
    num: "01",
    title: "Lettres & courriers",
    tagline: "Correction, réécriture, rédaction de lettres et courriers privés ou professionnels.",
    body: "Nous entretenons tous des rapports différents avec l'écriture. D'ordre privé ou professionnel, je corrige, réécris ou rédige vos lettres et courriers. Tout en respectant votre pensée, j'améliore le style et le vocabulaire.",
    img: "https://images.unsplash.com/photo-1473186505569-9c61870c11f9?auto=format&fit=crop&w=1200&q=80",
    alt: "Stylo plume posé sur une lettre manuscrite",
    questions: [
      { q: "Que souhaitez-vous ?", options: ["Faire corriger mon texte", "Faire réécrire mon texte", "Le faire rédiger entièrement"] },
      { q: "De quel type d'écrit s'agit-il ?", options: ["Courrier privé", "Courrier professionnel"] },
    ],
  },
  {
    id: "demarches",
    num: "02",
    title: "Démarches Web & administratives",
    tagline: "Web — Documents — Formulaires administratifs…",
    body: "Pour ceux que l'écrit apporte plus de questions que de réponses, je vous accompagne dans vos démarches administratives et professionnelles en remplissant vos documents et formulaires. Je rédige vos courriers et constitue pour vous divers dossiers auprès des services administratifs.",
    img: "https://images.unsplash.com/photo-1568909555141-aebfc970e8d0?auto=format&fit=crop&w=1200&q=80",
    alt: "Documents et formulaires posés sur un bureau",
    questions: [
      { q: "Quelle est votre démarche ?", options: ["Formulaire en ligne", "Courrier administratif", "Constitution d'un dossier"] },
      { q: "Pour quel cadre ?", options: ["Démarche personnelle", "Démarche professionnelle"] },
    ],
  },
  {
    id: "motivation-cv",
    num: "03",
    title: "Lettres de motivation & CV",
    tagline: "Correction et rédaction de lettres de motivation ou CV.",
    body: "Parce qu'ils sont le premier contact avec l'entreprise, votre lettre de motivation et votre CV sont des éléments essentiels. Je les corrige ou les écris pour vous.",
    img: "https://images.unsplash.com/photo-1518674660708-0e2c0473e68e?auto=format&fit=crop&w=1200&q=80",
    alt: "Stylo plume doré posé sur une feuille blanche",
    questions: [
      { q: "Quel document concerne votre demande ?", options: ["Lettre de motivation", "CV", "Les deux"] },
      { q: "Que souhaitez-vous ?", options: ["Une correction", "Une réécriture", "Une rédaction complète"] },
    ],
  },
  {
    id: "memoires",
    num: "04",
    title: "Mémoires & manuscrits",
    tagline: "Réécriture et correction de mémoires ou manuscrits.",
    body: "Écrire, c'est aussi donner de l'avenir au passé. Mémoire ou manuscrit ? Je vous accompagne dans l'écriture de votre vie, en écoutant vos mots comme vos silences.",
    img: "https://images.unsplash.com/photo-1447069387593-a5de0862481e?auto=format&fit=crop&w=1200&q=80",
    alt: "Anciennes lettres attachées et encrier sur une table",
    questions: [
      { q: "Quel est votre projet ?", options: ["Mémoire", "Manuscrit"] },
      { q: "Quel accompagnement souhaitez-vous ?", options: ["Relecture", "Correction", "Réécriture", "Un accompagnement complet"] },
    ],
  },
];

const METIER_IMG =
  "https://images.unsplash.com/photo-1561812938-f6e60cbf95e3?auto=format&fit=crop&w=1400&q=80";

export default function Home() {
  usePageMeta(
    "Les Mots pour le Dire — Écrivain public | Rédaction, correction, réécriture",
    "Écrivain public : rédaction, correction et réécriture de vos écrits — lettres et courriers, démarches Web et administratives, lettres de motivation et CV, mémoires et manuscrits."
  );

  const [openId, setOpenId] = useState(SERVICES[0].id);
  const [qcmService, setQcmService] = useState(null);

  return (
    <>
      <Hero />
      <Marquee items={MARQUEE_ITEMS} />

      {/* Signature */}
      <section className="py-24 md:py-36 bg-white" data-testid="signature-section">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <Reveal>
            <p className="eyebrow">Les Mots pour le Dire</p>
          </Reveal>
          <p className="font-serif italic text-2xl sm:text-3xl md:text-[2.6rem] leading-snug mt-7">
            <RevealLine>« Il faut avec les mots de tout le monde,</RevealLine>
            <RevealLine delay={0.12}>
              écrire comme <span className="text-vermilion">personne</span>… »
            </RevealLine>
          </p>
        </div>
      </section>

      {/* Écrivain public */}
      <section className="py-24 md:py-32 bg-paper-img border-y border-hairline" id="ecrivain-public" data-testid="metier-section">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid md:grid-cols-12 gap-12 md:gap-14 items-start">
          <div className="md:col-span-5 order-2 md:order-1">
            <Reveal>
              <div className="relative">
                <img
                  src={METIER_IMG}
                  alt="Écriture manuscrite à l'encre sur une feuille de papier"
                  loading="lazy"
                  className="w-full h-[240px] md:h-[300px] object-cover img-editorial border border-hairline"
                />
                <div className="absolute -bottom-5 left-5 bg-paper border border-hairline px-5 py-3.5" data-testid="metier-image-label">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-haze">
                    Un vieux métier d'avenir
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-7 order-1 md:order-2">
            <SectionHead eyebrow="L'écrivain public" title="Un vieux métier d'avenir" />
            <Reveal delay={0.15}>
              <p className="mt-6 text-soot leading-relaxed text-base md:text-lg">
                Aujourd'hui, l'écrivain public a un visage plus moderne et contemporain. Il propose un
                regard neutre et extérieur sur vos écrits, s'adapte à la société et joue souvent un rôle
                social.
              </p>
            </Reveal>
            <Reveal delay={0.22}>
              <p className="mt-5 text-soot leading-relaxed text-base md:text-lg">
                Il peut également être médiateur — le lien entre les usagers et les institutions. Sa
                mission principale : écrire pour ceux qui ne veulent pas, ou ne peuvent pas le faire.
              </p>
            </Reveal>
          </div>
          </div>
          <Reveal delay={0.15}>
            <div className="mt-16 md:mt-24 w-full border border-hairline bg-white px-7 py-8 md:px-10 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10 sm:justify-between" data-testid="metier-tarifs-block">
              <div>
                <p className="eyebrow">Tarifs</p>
                <p className="font-serif text-xl md:text-2xl tracking-tight mt-2 leading-snug">
                  Sur devis uniquement, pour un tarif juste et adapté à votre projet.
                </p>
                <p className="text-sm md:text-[15px] text-soot leading-relaxed mt-3 max-w-xl">
                  Chaque demande est estimée sur mesure : la nature de la prestation, la longueur du document et le délai souhaité déterminent le tarif. Décrivez-moi votre besoin en quelques lignes, vous recevrez un devis précis, sans engagement.
                </p>
              </div>
              <button
                type="button"
                onClick={() => scrollToTarget("#contact")}
                data-testid="tarifs-devis-button"
                className="btn-ghost shrink-0 !px-6 !py-3"
              >
                Demander un devis
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Services — accordéon */}
      <section className="py-24 md:py-32 bg-white" id="services" data-testid="services-section">
        <div className="mx-auto max-w-6xl px-6">
          <SectionHead
            eyebrow="Mes services"
            title="Ce que je fais pour vous"
            intro="Quatre formes d'accompagnement, une même exigence : des écrits justes, clairs et qui vous ressemblent. Dépliez la prestation qui vous concerne."
          />
          <Reveal delay={0.1} className="mt-14">
            <div className="border-t border-hairline" data-testid="services-accordion">
              {SERVICES.map((s) => {
                const open = openId === s.id;
                return (
                  <div key={s.id} className="border-b border-hairline" data-testid={`service-accordion-${s.id}`}>
                    <button
                      type="button"
                      onClick={() => setOpenId(open ? null : s.id)}
                      aria-expanded={open}
                      data-testid={`service-accordion-toggle-${s.id}`}
                      className="w-full flex items-center justify-between gap-6 text-left py-7 md:py-9 px-1 md:px-4 group hover:bg-paper-deep/50 transition-colors duration-300"
                    >
                      <span className="flex items-baseline gap-5 md:gap-8 min-w-0">
                        <span className="font-mono text-[11px] text-haze group-hover:text-vermilion transition-colors duration-300">
                          {s.num}
                        </span>
                        <span className="font-serif text-2xl md:text-4xl tracking-tight truncate">{s.title}</span>
                      </span>
                      <span className="relative w-9 h-9 md:w-10 md:h-10 border border-hairline shrink-0 group-hover:border-ink transition-colors duration-300">
                        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-px bg-ink" />
                        <span className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-4 w-px bg-ink transition-transform duration-300 ${open ? "rotate-90" : ""}`} />
                      </span>
                    </button>
                    <AnimatePresence initial={false}>
                      {open && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.45, ease: [0.215, 0.61, 0.355, 1] }}
                          className="overflow-hidden"
                        >
                          <div className="pb-10 md:pb-12 px-1 md:px-4 grid md:grid-cols-12 gap-8 items-start">
                            <img
                              src={s.img}
                              alt={s.alt}
                              loading="lazy"
                              className="md:col-span-4 w-full h-44 md:h-52 object-cover border border-hairline img-editorial"
                            />
                            <div className="md:col-span-8">
                              <p className="font-serif italic text-lg md:text-xl text-soot">{s.tagline}</p>
                              <p className="text-soot leading-relaxed mt-4 text-[15px] md:text-base max-w-2xl">{s.body}</p>
                              <button
                                type="button"
                                onClick={() => setQcmService(s)}
                                data-testid={`service-more-${s.id}`}
                                className="btn-primary mt-7"
                              >
                                En savoir plus
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Contact */}
      <section className="py-24 md:py-32 bg-paper-img border-t border-hairline" id="contact" data-testid="contact-section">
        <div className="mx-auto max-w-6xl px-6">
          <SectionHead
            align="center"
            eyebrow="Contact"
            title="Parlons de votre projet."
            intro="Il vous suffit de décrire votre besoin : une lettre à corriger, un formulaire à remplir, un CV à améliorer ou un manuscrit à relire. Je vous réponds personnellement."
          />
          <Reveal delay={0.16} className="mt-14">
            <div className="relative max-w-2xl mx-auto" data-testid="contact-form-card">
              <div className="absolute -top-7 left-1/2 -translate-x-1/2 w-14 h-14 bg-paper border border-hairline flex items-center justify-center text-vermilion">
                <Feather size={18} />
              </div>
              <div className="bg-white border border-hairline p-8 md:p-12 pt-12 md:pt-14 shadow-[0_24px_60px_rgba(20,20,20,0.06)]">
                <ContactForm />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.24}>
            <p className="text-center font-mono text-[10px] uppercase tracking-[0.25em] text-haze mt-10">
              Chaque demande est lue et traitée personnellement — vos écrits restent confidentiels
            </p>
          </Reveal>
        </div>
      </section>

      <AnimatePresence>
        {qcmService && <QcmModal key="qcm" service={qcmService} onClose={() => setQcmService(null)} />}
      </AnimatePresence>
    </>
  );
}
